
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { toast } = useToast();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log("Auth state change event:", event);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (event === 'SIGNED_IN') {
          const subscriptionIntent = localStorage.getItem('subscriptionIntent');
          if (subscriptionIntent) {
            localStorage.removeItem('subscriptionIntent');
            
            // Give browser time to complete session setup before redirecting
            setTimeout(() => {
              window.location.href = '/subscribe';
            }, 300);
          } else {
            toast({
              title: "Welcome back!",
              description: "You have successfully signed in.",
            });
          }
        } else if (event === 'USER_UPDATED') {
          // When the user is updated (email verification), check for subscription intent
          const subscriptionIntent = localStorage.getItem('subscriptionIntent');
          if (subscriptionIntent) {
            localStorage.removeItem('subscriptionIntent');
            
            // Give browser time to complete session setup before redirecting
            setTimeout(() => {
              window.location.href = '/subscribe';
            }, 300);
          }
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      console.log("Initial session check:", currentSession ? "Session exists" : "No session");
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    });

    // Check for URL parameters that might indicate a post-verification redirect
    const checkForRedirectParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('type') && urlParams.has('access_token')) {
        const type = urlParams.get('type');
        if (type === 'recovery' || type === 'signup') {
          console.log("Found redirect params:", type);
          // If we detect verification params and have a subscription intent, preserve it
          if (localStorage.getItem('subscriptionIntent')) {
            console.log("Preserving subscription intent through verification");
          }
        }
      }
    };
    
    checkForRedirectParams();

    return () => subscription.unsubscribe();
  }, [toast]);

  const signOut = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Signed out",
      description: "You have been signed out successfully.",
    });
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
